import { useQuery } from 'react-query';

import feathersClient from '../../apiClient';
import { useUser } from '../../apiClient/ApiContext';
import { CertificateIcon, ListIcon } from '../../components/icons';
import { Roles } from '../../utils/enums';
import Tymbe from '../partials/Tymbe';
import MenuItem from '../../components/Menu/MenuItem';
import MenuTest from '../../components/Menu/SubMenu';
import { getPaginatedResponse } from '../../apiClient/utils';
import { ActionIcon, ContentIcon, FavouriteIcon, FileIcon, NavigationIcon } from '@tymbe/ui-icons';
import UserPanel from './UserPanel';
import Chat from './Chat';

const Sidebar = () => {
  const host = window ? window.location.host : null;
  const isStage = host === 'admin.tytodas.cz' || host === 'admin2.tytodas.cz';
  const isLocal = host && ['127.0.0.1:5173', '127.0.0.1:5174', 'localhost:5173', 'localhost:5174'].includes(host);
  const user = useUser();
  const isAdmin = user.hasRoles([
    Roles.SUPER_ADMIN,
    Roles.TYMBE_ADMIN,
    Roles.ADMIN,
    Roles.TYMBE_COORDINATOR,
  ]);

  const { data: unresolvedPayments } = useQuery(['sidebarUnresolvedPayments'], async () => {
    const resp = await feathersClient.service('payment-request').find({
      query: { $modify: 'unpaid', $limit: 0 },
    });
    return getPaginatedResponse(resp);
  });

  const { data: waitingDocuments } = useQuery(['sidebarWaitingDocuments'], async () => {
    const resp = await feathersClient.service('person-document').find({
      query: { approved: { $null: true }, $limit: 0 },
    });
    return getPaginatedResponse(resp);
  });

  const { data: mpsvReportErrors } = useQuery(['mpsvReportErrors'], async () => {
    const resp = await feathersClient.service('application').find({
      query: {
        $limit: 0,
        $modify: 'mpsvScheduled',
      },
    });
    return getPaginatedResponse(resp);
  });

  const loggedUserCompanyId = user?.person?.company?.[0]?.id;

  return (
    <div data-name="SideBar" className="flex flex-col h-full w-64 shrink-0 grow-0">
      <div className="flex flex-col justify-end gap-3 sticky bg-base-back-1 shrink-0">
        <Tymbe className="pl-10 pb-8 pt-14 relative" />
      </div>

      {isLocal && (
        <div className="bg-primary-300 py-2 text-center text-heading-4-xl">
          LOCALHOST <span role="img" aria-label="robot">🤖</span>
        </div>
      )}
      {isStage && (
        <div className="bg-danger py-2 text-center text-heading-4-xl">
          TESTOVACÍ VERZE
        </div>
      )}

      <div className="overflow-auto flex flex-col -mr-1 [scrollbar-gutter:stable]">
        <MenuItem
          icon={<FavouriteIcon name="calendar_today" />}
          to="/calendar"
        >
          Kalendář
        </MenuItem>
        <MenuItem
          icon={<FavouriteIcon name="list_alt" />}
          to="/shift"
        >
          Objednávky
        </MenuItem>
        <MenuItem
          icon={<FavouriteIcon name="description" />}
          to="/attendance"
        >
          Docházka
        </MenuItem>
        <MenuItem
          icon={<FavouriteIcon name="people" />}
          to="/tymber"
        >
          Tymbeři
        </MenuItem>
        {isAdmin
          && (
            <MenuItem
              icon={<NavigationIcon name="home_work" />}
              to="/company"
            >
              Firma
            </MenuItem>
          )}
        <MenuItem
          icon={<ContentIcon name="block" />}
          to="/blocks"
          hidden={!user.hasRoles([
            Roles.SUPER_ADMIN,
            Roles.TYMBE_ADMIN,
            Roles.ADMIN,
            Roles.TYMBE_COORDINATOR,
            Roles.COMPANY,
            Roles.BRANCHOFFICE_MANAGER,
          ])}
        >
          Blokace
        </MenuItem>
        <MenuItem
          icon={<ListIcon />}
          to="/education"
          hidden
        >
          Seznam školení
        </MenuItem>
        {isAdmin ? (
          <>
            <MenuItem
              to="/monthly-wage"
              icon={<FileIcon name="newspaper" />}
            >
              Měsíční DPP
            </MenuItem>
            <MenuItem
              to="/admin-shift"
              icon={<ActionIcon name="work_outline" />}
            >
              Směny
            </MenuItem>
            <MenuTest
              hidden={!user.hasRoles([Roles.SUPER_ADMIN, Roles.TYMBE_ADMIN])}
              title="Administrace"
              icon={<ActionIcon name="list" />}
            >
              <MenuItem to="/administration/shortages">
                Manka
              </MenuItem>
              <MenuItem
                to="/administration/payment"
              >
                Výplaty (
                {unresolvedPayments?.total || 0}
                )
              </MenuItem>
              <MenuItem
                to="/administration/position"
              >
                Pozice
              </MenuItem>
              <MenuItem
                to="/administration/foreclosures"
              >
                Exekuce
              </MenuItem>
              <MenuItem
                to="/admin-administration-documents"
              >
                Dokumenty (
                {waitingDocuments?.total || 0}
                )
              </MenuItem>
              <MenuItem
                to="/administration/utility"
              >
                Pomůcky
              </MenuItem>
              <MenuItem
                to="/administration/email"
              >
                Emaily
              </MenuItem>
              <MenuItem
                to="/administration/reporting"
              >
                Reporting
              </MenuItem>
              <MenuItem
                to="/administration/mpsv"
              >
                MPSV ({mpsvReportErrors?.total || 0})
              </MenuItem>
              <MenuItem
                to="/administration/internal-users"
              >
                Interní uživatelé
              </MenuItem>
              <MenuItem
                to="/administration/absence"
              >
                Absence
              </MenuItem>
            </MenuTest>
          </>
        ) : (
          <MenuItem
            icon={<CertificateIcon />}
            to={`/company/${loggedUserCompanyId}`}
          >
            Firma
          </MenuItem>
        )}
      </div>
      <div className="shrink grow" />
      <Chat />
      <UserPanel />
    </div>
  );
};

export default Sidebar;
